import * as C from './constants'
import type {
  Entity,
  EntityKind,
  GameEvent,
  GameState,
  Lane,
  ObstacleKind,
  PowerupKind,
  RunStats,
} from './types'

/* ------------------------------------------------------------------ rng --- */

/** Deterministic PRNG so a run can be replayed from a seed in a smoke test. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/* ------------------------------------------------------------- lifecycle -- */

function emptyStats(): RunStats {
  return {
    orders: 0,
    fuel: 0,
    bobi: 0,
    sokar: 0,
    nearMisses: 0,
    dodgedByBobi: 0,
    distance: 0,
    time: 0,
    topSpeed: 0,
  }
}

export function createGame(seed = Date.now()): GameState {
  return {
    status: 'ready',
    time: 0,
    distance: 0,
    speed: C.SPEED_START,
    lane: 1,
    fromX: 0,
    laneT: 1,
    x: 0,
    entities: [],
    score: 0,
    combo: 0,
    comboTimer: 0,
    autopilot: 0,
    doublePoints: 0,
    crashKind: null,
    crashFx: 0,
    stats: emptyStats(),
    events: [],
    spawnIn: C.GRACE_DISTANCE,
    powerupCooldown: C.POWERUP_COOLDOWN,
    sinceLaneChange: 99,
    lastLane: 1,
    lastFreeLane: 1,
    nextId: 1,
    rng: mulberry32(seed),
  }
}

/** Resets an existing state object in place so the render loop keeps its reference. */
export function startRun(s: GameState, seed = Date.now()): void {
  s.status = 'running'
  s.time = 0
  s.distance = 0
  s.speed = C.SPEED_START
  s.lane = 1
  s.fromX = 0
  s.laneT = 1
  s.x = 0
  s.entities.length = 0
  s.score = 0
  s.combo = 0
  s.comboTimer = 0
  s.autopilot = 0
  s.doublePoints = 0
  s.crashKind = null
  s.crashFx = 0
  s.stats = emptyStats()
  s.events.length = 0
  s.spawnIn = C.GRACE_DISTANCE
  s.powerupCooldown = C.POWERUP_COOLDOWN
  s.sinceLaneChange = 99
  s.lastLane = 1
  s.lastFreeLane = 1
  s.nextId = 1
  s.rng = mulberry32(seed)
}

export function drainEvents(s: GameState): GameEvent[] {
  if (s.events.length === 0) return []
  return s.events.splice(0, s.events.length)
}

/* ---------------------------------------------------------------- score --- */

/** Combo multiplier, including the Sokar double-points window. */
export function multiplier(s: GameState): number {
  const base = 1 + s.combo * C.COMBO_STEP
  return s.doublePoints > 0 ? base * 2 : base
}

function award(s: GameState, points: number): number {
  const gained = points * multiplier(s)
  s.score += gained
  return gained
}

/* ---------------------------------------------------------------- input --- */

function moveTo(s: GameState, target: Lane): void {
  if (target === s.lane) return
  s.lastLane = s.lane
  s.fromX = s.x
  s.lane = target
  s.laneT = 0
  s.sinceLaneChange = 0
}

/**
 * Player steering. Ignored while Bobi drives — that is the whole point of the
 * autopilot power-up: the assistant takes over.
 */
export function steer(s: GameState, dir: -1 | 1): boolean {
  if (s.status !== 'running' || s.autopilot > 0) return false
  const target = Math.min(2, Math.max(0, s.lane + dir)) as Lane
  if (target === s.lane) return false
  moveTo(s, target)
  s.events.push({ kind: 'lane' })
  return true
}

/* ---------------------------------------------------------------- spawns -- */

/** 0 → 1 difficulty, saturating at `HEADWAY_RAMP_TIME`. */
function difficulty(s: GameState): number {
  return Math.min(1, s.time / C.HEADWAY_RAMP_TIME)
}

function addEntity(s: GameState, kind: EntityKind, lane: Lane, z: number): Entity {
  const group =
    kind === 'order' || kind === 'fuel' ? 'pickup'
    : kind === 'bobi' || kind === 'sokar' ? 'powerup'
    : 'obstacle'
  const e: Entity = { id: s.nextId++, group, kind, lane, z, spent: false, fx: 0 }
  s.entities.push(e)
  return e
}

/**
 * Builds one row of entities, always leaving at least one lane open.
 *
 * A two-obstacle row pins the player to a single lane, so its gap must satisfy
 * two reachability constraints at once:
 *   - within one lane of where the truck is now, so the player can get there
 *     during the ~3 s the row takes to arrive;
 *   - within one lane of the previous row's gap, so two tight rows in a row
 *     never demand a double lane change.
 * Those two neighbourhoods always intersect (lanes are at most 2 apart), so a
 * legal gap always exists and no run can be lost to an impossible pattern.
 */
function spawnRow(s: GameState): void {
  const wantsTwo = s.rng() < difficulty(s) * C.DOUBLE_OBSTACLE_MAX_CHANCE
  const reachable = C.LANES.filter(
    (l) => Math.abs(l - s.lane) <= 1 && Math.abs(l - s.lastFreeLane) <= 1,
  )
  const freeLane = (
    wantsTwo ?
      reachable[Math.floor(s.rng() * reachable.length)]
    : C.LANES[Math.floor(s.rng() * C.LANES.length)]) as Lane

  const others = C.LANES.filter((l) => l !== freeLane)
  const blocked: Lane[] = wantsTwo ? others : [others[Math.floor(s.rng() * others.length)]]

  for (const lane of blocked) {
    const kind = C.OBSTACLE_KINDS[Math.floor(s.rng() * C.OBSTACLE_KINDS.length)]
    addEntity(s, kind, lane, C.SPAWN_Z + (s.rng() - 0.5) * 6)
  }

  const open = C.LANES.filter((l) => !blocked.includes(l))
  let powerupLane: Lane | null = null
  if (s.powerupCooldown <= 0 && s.rng() < C.POWERUP_CHANCE) {
    powerupLane = open[Math.floor(s.rng() * open.length)]
    const kind: PowerupKind = s.rng() < C.BOBI_SHARE ? 'bobi' : 'sokar'
    addEntity(s, kind, powerupLane, C.SPAWN_Z)
    s.powerupCooldown = C.POWERUP_COOLDOWN
  }

  for (const lane of open) {
    if (lane === powerupLane) continue
    const roll = s.rng()
    if (roll < C.ORDER_CHANCE) addEntity(s, 'order', lane, C.SPAWN_Z + (s.rng() - 0.5) * 8)
    else if (roll < C.ORDER_CHANCE + C.FUEL_CHANCE)
      addEntity(s, 'fuel', lane, C.SPAWN_Z + (s.rng() - 0.5) * 8)
  }

  // Single-obstacle rows leave two gaps; remember the one the player is most
  // likely to take, so the chain constraint above stays meaningful.
  s.lastFreeLane = open.reduce((a, b) => (Math.abs(b - s.lane) < Math.abs(a - s.lane) ? b : a))
}

/* ------------------------------------------------------------- autopilot -- */

/** Distance ahead Bobi looks when choosing a lane. */
const AUTOPILOT_LOOKAHEAD = 75

/**
 * Picks where Bobi steers next. Only the current lane and its immediate
 * neighbours are candidates, because a rig changes one lane at a time; and a
 * neighbour is only eligible once its obstacles have cleared the whole 9 m
 * rig, since the trailer still occupies road long after the cab has passed.
 */
function autopilotLane(s: GameState): Lane {
  let best: Lane = s.lane
  let bestScore = -Infinity
  for (const lane of C.LANES) {
    if (Math.abs(lane - s.lane) > 1) continue
    let nearestObstacle = AUTOPILOT_LOOKAHEAD
    let loot = 0
    let alongside = false
    for (const e of s.entities) {
      if (e.spent || e.lane !== lane || e.z > AUTOPILOT_LOOKAHEAD) continue
      if (e.z < 0) {
        if (e.group === 'obstacle' && e.z + C.VISUALS[e.kind].depth / 2 > -C.TRUCK_DEPTH)
          alongside = true
        continue
      }
      if (e.group === 'obstacle') nearestObstacle = Math.min(nearestObstacle, e.z)
      else loot += e.group === 'powerup' ? 30 : 12
    }
    if (alongside && lane !== s.lane) continue
    const score = nearestObstacle + loot - Math.abs(lane - s.lane) * 6
    if (score > bestScore) {
      bestScore = score
      best = lane
    }
  }
  return best
}

/* ------------------------------------------------------------ collisions -- */

function overlapsZ(z: number, depth: number): boolean {
  return z - depth / 2 < 0 && z + depth / 2 > -C.TRUCK_DEPTH
}

function overlapsX(s: GameState, e: Entity, tolerance: number): boolean {
  const visual = C.VISUALS[e.kind]
  const reach = (C.TRUCK_HALF_WIDTH + visual.halfWidth) * tolerance
  return Math.abs(s.x - C.LANE_X[e.lane]) < reach
}

function crash(s: GameState, kind: ObstacleKind): void {
  s.status = 'crashed'
  s.crashKind = kind
  s.crashFx = 1
  s.stats.distance = s.distance
  s.stats.time = s.time
  s.events.push({ kind: 'crash', entityKind: kind })
}

function collectSokar(s: GameState): void {
  for (const e of s.entities) {
    if (e.spent || e.group !== 'obstacle' || e.z <= 0 || e.z > C.SOKAR_CLEAR_RANGE) continue
    e.spent = true
    e.fx = 1
    award(s, C.POINTS_SOKAR_CLEAR)
  }
}

function resolveEntity(s: GameState, e: Entity): void {
  const visual = C.VISUALS[e.kind]

  if (e.group === 'obstacle') {
    if (overlapsZ(e.z, visual.depth) && overlapsX(s, e, 0.85)) {
      if (s.autopilot > 0) {
        e.spent = true
        e.fx = 1
        s.stats.dodgedByBobi++
        const points = award(s, C.POINTS_AUTOPILOT_DODGE)
        s.events.push({ kind: 'autopilotDodge', entityKind: e.kind, points })
      } else {
        crash(s, e.kind as ObstacleKind)
      }
    }
    return
  }

  if (!overlapsZ(e.z, visual.depth) || !overlapsX(s, e, 1.25)) return
  e.spent = true
  e.fx = 1

  switch (e.kind) {
    case 'order': {
      s.stats.orders++
      const points = award(s, C.POINTS_ORDER)
      s.events.push({ kind: 'order', points })
      break
    }
    case 'fuel': {
      s.stats.fuel++
      const points = award(s, C.POINTS_FUEL)
      s.events.push({ kind: 'fuel', points })
      if (s.combo < C.MAX_COMBO) {
        s.combo++
        s.events.push({ kind: 'comboUp', combo: s.combo })
      }
      s.comboTimer = C.COMBO_TIMEOUT
      break
    }
    case 'bobi': {
      s.stats.bobi++
      s.autopilot = C.AUTOPILOT_TIME
      s.events.push({ kind: 'bobi', entityKind: 'bobi' })
      break
    }
    case 'sokar': {
      s.stats.sokar++
      s.doublePoints = C.DOUBLE_POINTS_TIME
      collectSokar(s)
      s.events.push({ kind: 'sokar', entityKind: 'sokar' })
      break
    }
  }
}

/* ----------------------------------------------------------------- tick --- */

function easeOutCubic(t: number): number {
  const u = 1 - t
  return 1 - u * u * u
}

/** Advances the simulation by one fixed step. `dt` is in seconds. */
export function update(s: GameState, dt: number): void {
  if (s.crashFx > 0 && s.status !== 'running') s.crashFx = Math.max(0, s.crashFx - dt * 1.6)
  if (s.status !== 'running') return

  s.time += dt
  s.sinceLaneChange += dt
  s.speed = Math.min(C.SPEED_MAX, C.SPEED_START + C.SPEED_RAMP * s.time)
  const kmh = C.speedo(s.speed)
  if (kmh > s.stats.topSpeed) s.stats.topSpeed = kmh

  const ds = s.speed * dt
  s.distance += ds
  award(s, ds * C.POINTS_PER_METRE)

  if (s.laneT < 1) {
    s.laneT = Math.min(1, s.laneT + dt / C.LANE_CHANGE_TIME)
    s.x = s.fromX + (C.LANE_X[s.lane] - s.fromX) * easeOutCubic(s.laneT)
  } else {
    s.x = C.LANE_X[s.lane]
  }

  if (s.autopilot > 0) {
    s.autopilot -= dt
    if (s.autopilot <= 0) {
      s.autopilot = 0
      s.events.push({ kind: 'powerupEnd', entityKind: 'bobi' })
    } else if (s.laneT >= 1) {
      const target = autopilotLane(s)
      if (target !== s.lane) moveTo(s, target)
    }
  }

  if (s.doublePoints > 0) {
    s.doublePoints -= dt
    if (s.doublePoints <= 0) {
      s.doublePoints = 0
      s.events.push({ kind: 'powerupEnd', entityKind: 'sokar' })
    }
  }

  if (s.combo > 0) {
    s.comboTimer -= dt
    if (s.comboTimer <= 0) {
      s.combo--
      s.comboTimer = C.COMBO_TIMEOUT
      s.events.push({ kind: 'comboLost', combo: s.combo })
    }
  }

  if (s.powerupCooldown > 0) s.powerupCooldown -= dt

  for (let i = s.entities.length - 1; i >= 0; i--) {
    const e = s.entities[i]
    const wasAhead = e.z > 0
    e.z -= ds
    if (e.fx > 0) e.fx = Math.max(0, e.fx - dt * 3)

    if (!e.spent) {
      resolveEntity(s, e)
      if (s.status !== 'running') return

      if (
        !e.spent &&
        e.group === 'obstacle' &&
        wasAhead &&
        e.z <= 0 &&
        s.sinceLaneChange < C.NEAR_MISS_WINDOW &&
        e.lane === s.lastLane
      ) {
        s.stats.nearMisses++
        const points = award(s, C.POINTS_NEAR_MISS)
        s.events.push({ kind: 'nearMiss', entityKind: e.kind, points })
      }
    }

    if (e.z < C.CULL_Z) s.entities.splice(i, 1)
  }

  s.spawnIn -= ds
  if (s.spawnIn <= 0) {
    spawnRow(s)
    s.spawnIn = s.speed * (C.HEADWAY_START + (C.HEADWAY_MIN - C.HEADWAY_START) * difficulty(s))
  }

  s.stats.distance = s.distance
  s.stats.time = s.time
}

/**
 * Advances the simulation by an arbitrary amount of real time using fixed
 * steps, so physics stay identical regardless of display refresh rate.
 */
export function advance(s: GameState, elapsed: number): void {
  let remaining = Math.min(elapsed, C.MAX_FRAME_TIME)
  while (remaining > 0) {
    const step = Math.min(C.FIXED_STEP, remaining)
    update(s, step)
    remaining -= step
  }
}
